<script setup lang="ts">
import 'vue-skeletor/dist/vue-skeletor.css';
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PropertyImage from '@properties/components/PropertyImage.vue';
import {
  ADVERT_TYPE_BTR,
  ADVERT_TYPE_HMO,
  ADVERT_TYPE_SPARE_ROOM,
} from '@properties/constants';

type PropertyRowCellProps = {
  property: Domain.Properties.DataObjects.PropertyData;
  size?: 'small' | 'default' | 'large';
};

const props = withDefaults(defineProps<PropertyRowCellProps>(), {
  size: 'default',
});

const propertyName = computed<string | null>(
  () => props.property?.propertyName,
);
const addressLineOne = computed<string | undefined>(
  () => props.property?.shortAddress,
);
const addressLineTwo = computed<string>(
  () => props.property?.area?.name + ', ' + props.property?.postcode,
);

const isHMO = computed<boolean>(() => {
  return props.property?.advert_type === ADVERT_TYPE_HMO;
});

const isBTROrSpareRoom = computed<boolean>(() => {
  return (
    props.property?.advert_type === ADVERT_TYPE_BTR ||
    props.property?.advert_type === ADVERT_TYPE_SPARE_ROOM
  );
});

const isSpareRoom = computed<boolean>(() => {
  return props.property?.advert_type === ADVERT_TYPE_SPARE_ROOM;
});

const { cdnAsset } = useHelpers();
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex gap-4">
      <PropertyImage :image-url="props.property.imageUrl" :size="props.size" />

      <!-- HMO -->
      <div
        v-if="isHMO"
        class="flex flex-1 flex-col gap-1 overflow-hidden px-4 py-0"
      >
        <p
          class="whitespace-pre-wrap text-sm font-normal leading-[1.5] text-gray-900"
        >
          {{ addressLineOne }}
        </p>
        <p
          class="whitespace-pre-wrap text-sm font-normal leading-[1.5] text-gray-500"
        >
          {{ addressLineTwo }}
        </p>
        <div class="flex items-center gap-3">
          <div class="flex flex-wrap items-center gap-3">
            <div class="flex items-center gap-1">
              <div class="flex h-5 items-center justify-center">
                <FontAwesomeIcon
                  :icon="['fas', 'bath']"
                  class="text-base leading-[0] text-gray-300"
                />
              </div>
              <p
                class="overflow-hidden overflow-ellipsis text-xs font-normal leading-[1.5] text-gray-500"
              >
                {{ props.property.bathrooms }}
              </p>
            </div>
            <div class="flex items-center gap-1">
              <div class="flex h-5 items-center justify-center">
                <FontAwesomeIcon
                  :icon="['fas', 'bed']"
                  class="text-base leading-[0] text-gray-300"
                />
              </div>
              <p
                class="overflow-hidden overflow-ellipsis text-xs font-normal leading-[1.5] text-gray-500"
              >
                {{ props.property.roomCount }}
              </p>
            </div>
          </div>
          <div
            v-if="props.property.isFeedProperty && props.property.feedReference"
            class="flex items-center gap-1"
          >
            <div class="flex items-start">
              <FontAwesomeIcon
                :icon="['fas', 'house-signal']"
                class="text-base leading-[0] text-gray-300"
              />
            </div>
            <p class="text-xs font-normal leading-[1.5] text-gray-500">
              Ref: {{ (props.property.feedReference as any)?.feed_reference }}
            </p>
            <pre class="hidden">{{ props.property.feedReference }}</pre>
          </div>
        </div>
      </div>

      <!-- BTR/Spare Room -->
      <div
        v-else-if="isBTROrSpareRoom"
        class="flex flex-1 flex-col gap-1 overflow-hidden px-4 py-0"
      >
        <p
          v-if="propertyName"
          class="whitespace-pre-wrap text-sm font-normal leading-[1.5] text-gray-900"
          title="Building/Property Name"
        >
          {{ propertyName }}
        </p>
        <p
          class="overflow-hidden overflow-ellipsis whitespace-nowrap text-sm font-normal leading-[1.5] text-gray-900"
        >
          {{ addressLineOne }}
        </p>
        <p
          class="overflow-hidden overflow-ellipsis whitespace-nowrap text-sm font-normal leading-[1.5] text-gray-500"
        >
          {{ addressLineTwo }}
        </p>
        <div class="flex flex-col gap-1 mt-1">
          <div class="flex flex-wrap items-center gap-3">
            <div
              v-if="isSpareRoom"
              class="dark:text-gray-200 md:pl-[6px] flex w-fit items-center space-x-1 rounded-[5px] bg-gray-100 px-[8px] py-0 text-[12px] font-normal leading-[27px] text-gray-700"
            >
              <div class="tw-w-[15px] md:tw-w-auto">
                <img
                  alt="Spare Room Icon"
                  class="tw-h-[24px] tw-w-[24px] opacity-60"
                  :src="cdnAsset('svg/phosphor/door-open-gray.svg')"
                />
              </div>
              <span>Spare room</span>
            </div>
            <div class="flex items-center gap-1">
              <div class="flex h-5 items-center justify-center">
                <FontAwesomeIcon
                  :icon="['fas', 'bed']"
                  class="text-base leading-[0] text-gray-300"
                />
              </div>
              <p
                class="overflow-hidden overflow-ellipsis text-xs font-normal leading-[1.5] text-gray-500"
              >
                {{ props.property.roomCount }}
                {{
                  props.property?.advert_type === ADVERT_TYPE_SPARE_ROOM
                    ? 'rooms'
                    : 'unit types'
                }}
              </p>
            </div>
          </div>
          <div
            v-if="props.property.isFeedProperty && props.property.feedReference"
            class="flex items-center gap-1"
          >
            <div class="flex items-start">
              <FontAwesomeIcon
                :icon="['fas', 'house-signal']"
                class="text-base leading-[0] text-gray-300"
              />
            </div>
            <p class="text-xs font-normal leading-[1.5] text-gray-500">
              Ref: {{ (props.property.feedReference as any)?.feed_reference }}
            </p>
            <pre class="hidden">{{ props.property.feedReference }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
